import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { getMasterAdminBillingPath } from '@/bundle/_MasterAdmin/_BillingPage/path/path';
import { Button } from '@/components/Button/Button';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { BillingEventsTable } from './ui/BillingEventsTable';
import { BillingEventsFilterList } from './ui/BillingEventsFilterList';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { saveAs } from 'file-saver';
import { getInternalOrganizationBillingEvents, getInternalOrganizationBillingReport } from './api/api';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { BillingEventsFilterParamsType, useBillingEventsFilters } from './hooks/useBillingEventsFilters';
import { QueryParamsType } from '@/api/helpers/apiParamsHelpers';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';
import { useEffect } from 'react';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { getFileNameFromHeader } from '@/helpers/fileHelpers';

export const BillingEventsPage = () => {
  const { navigateBack } = useStatefulNavigation();
  const searchParams = useGetSearchParams<BillingEventsFilterParamsType>();
  const { queryFilters, filters, applyFilters, clearFilters } = useBillingEventsFilters();

  const { data, isPending } = useQuery({
    queryKey: ['get_internal_organization_billing_events', searchParams, queryFilters],
    queryFn: () =>
      getInternalOrganizationBillingEvents({
        ...searchParams,
        filters: queryFilters,
      }),
    placeholderData: keepPreviousData,
    enabled: !!searchParams.filters,
  });

  const { mutate: getBillingReportMutate, isPending: isPendingBillingReport } = useMutation({
    mutationKey: ['get_internal_organization_billing_report'],
    mutationFn: () => {
      const payload = {
        ...queryFilters,
      };

      return getInternalOrganizationBillingReport(payload);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      const header = response.headers.contentDisposition;
      const fileName = getFileNameFromHeader(header, 'Billing.xlsx');

      saveAs(response?.body, fileName);
    },
  });

  const { list, totalCount, hasPagination } = getListData(
    data?.body,
    searchParams as QueryParamsType & BillingEventsFilterParamsType
  );

  useEffect(() => {
    const billedEventsError = data?.error;

    if (!billedEventsError) return;

    const error = getResponseError(billedEventsError) || getResponseError(billedEventsError, 'date_created');

    showSnackbar(error, { variant: 'error', autoClose: false });
  }, [data?.error]);

  return (
    <ContentLayout
      title='Billing Report'
      backButton='Back to Billing'
      breadcrumbs='Billing > Report'
      onBack={() => navigateBack(getMasterAdminBillingPath())}
      headerAction={
        <Button
          width={140}
          onClick={getBillingReportMutate}
          mobileStretch
          disabled={isPendingBillingReport || !totalCount}
        >
          {isPendingBillingReport ? 'Exporting...' : 'Export'}
        </Button>
      }
      subtitle={
        <>
          <>
            View the list of billable events for the selected time period.
            <br />
            By default billable events for the previous calendar month are displayed. These billable events will be
            included in the report.
          </>
        </>
      }
    >
      <PageBody>
        {filters && (
          <BillingEventsFilterList filters={filters} onApplyFilters={applyFilters} onClearFilters={clearFilters} />
        )}
        <BillingEventsTable items={list} isLoading={isPending} />
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}
    </ContentLayout>
  );
};
